import { observer } from 'mobx-react';
import React, { Component } from 'react';
import { Route, Routes } from 'react-router-dom';
import ErrorBox from 'components/ErrorBox';
import WebWidget from 'components/forms/WebWidget';
import Loading from 'components/Loading';
import ProfileNavigate from 'components/ProfileNavigate';
import APP from 'stores/APP';
import security from 'stores/security';
import classlist from 'utils/classlist';
import { language } from 'utils/i18n';
import withRouter, { RouteComponentProps } from 'utils/withRouter';
import Account from '../Account';
import Login from '../Login';
import Logout from '../Logout';
import Page404 from '../Page404';
import Environment from './Environment';
import Profile from './Profile';
import SiteFooter from './SiteFooter';
import SiteHeader from './SiteHeader';
import './style.scss';

interface AppProps {
    isEmbedded?: boolean;
}

interface AppStates {
    error: Error | null;
    loading: boolean;
}

@observer
class App extends Component<RouteComponentProps & AppProps, AppStates> {
    readonly state: AppStates = {
        loading: true,
        error: null
    };

    componentDidMount() {
        this.getStatus();
    }

    getStatus = async () => {
        try {
            this.setState({
                error: null,
                loading: true
            });

            await security.ready();
        } catch (error) {
            if (error.error_code && this.isNeedLogin(error.error_code)) {
                // 检测到因为token问题引发的登录问题时，需要清除token
                // token过期或无效时，不主动清理deviceid
                security.clearAuth(false);
            } else {
                this.setState({
                    error
                });
            }
        }

        this.setState({
            loading: false
        });
    };

    /**
     * @description 10501 - 10509 表示token 相关错误，可能用户的token已过期，需要引导重新登录
     * @description 错误码参考文档：https://wiki.tigerbrokers.net/pages/viewpage.action?pageId=6849725
     * @param error_code 错误码
     */
    isNeedLogin(error_code) {
        return error_code >= 10501 && error_code <= 10509;
    }

    renderRoutes() {
        const { error, loading } = this.state;

        if (loading) {
            return <Loading className="site-app-loading" />;
        }

        if (error) {
            return <ErrorBox error={error} onClick={this.getStatus} />;
        }

        return (
            <Routes>
                {/** login下也需要存在该目录，用于重定向到next */}
                <Route path="/login" element={<Login />} />
                <Route path="/logout" element={<Logout />} />
                {security.isLogin && <Route path="/profile/*" element={<Profile />} />}
                <Route path="/account/*" element={<Account />} />
                <Route
                    path="*"
                    element={security.isLogin ? <ProfileNavigate to="/profile" /> : <Page404 autoRedirect />}
                />
            </Routes>
        );
    }

    render() {
        const { isEmbedded } = this.props;
        const { pathname } = this.props.location;
        const noSiteHeader = pathname.startsWith('/login') || pathname.startsWith('/logout');
        const fullscreenMode = noSiteHeader || pathname.startsWith('/account');
        const showHeader = !isEmbedded && !APP.isTiger && !noSiteHeader;

        return (
            <div
                className={classlist(
                    'portal-root',
                    { 'fullscreen-container': fullscreenMode },
                    { embedded: isEmbedded },
                    `tiger-intl-${language.startsWith('en') ? 'en_US' : language}`
                )}>
                {showHeader && <SiteHeader />}
                <section className="site-container">{this.renderRoutes()}</section>
                {showHeader && <SiteFooter />}
                {!this.state.loading && !APP.isTiger && (
                    <WebWidget isMarscoAccount={security.isMarscoAccount} isLogin={security.isLogin} />
                )}
                {!APP.isTiger && <Environment />}
            </div>
        );
    }
}

export default withRouter(App);
